import { PublicClientApplication } from "@azure/msal-browser";
import { fetchAndStoreSessionToken } from "../api/api";

export type B2CClaims = {
  oid?: string;
  emails?: string[];
  [key: string]: unknown;
};

/**
 * Teams Authentication Token Storage
 *
 * Stores the encrypted token obtained via Teams popup authentication in localStorage.
 * The token is Fernet-encrypted and only the backend can decrypt it.
 * This follows Microsoft's recommendation to never pass tokens directly via notifySuccess.
 *
 * In Teams, the app runs in an iframe so MSAL's localStorage cache isn't accessible
 * from the main window. We store the encrypted token from the Teams auth popup here instead.
 * The backend will decrypt and validate the actual B2C token on each request.
 */
const TEAMS_TOKEN_KEY = "teams.encrypted.token";
const TEAMS_TOKEN_EXPIRY_KEY = "teams.token.expiry";

// Buffer time before actual expiry to trigger refresh (60 seconds)
const EXPIRY_BUFFER_MS = 60 * 1000;

/**
 * Store the Teams access token along with its expiry time.
 * @param token - The encrypted token string
 * @param expiration - The access token expiry from AuthenticationResult.expiresOn (Unix timestamp in seconds)
 */
export function setTeamsAccessToken(token: string | null, expiration?: number) {
  if (token) {
    localStorage.setItem(TEAMS_TOKEN_KEY, token);
    if (expiration) {
      localStorage.setItem(TEAMS_TOKEN_EXPIRY_KEY, String(expiration * 1000));
    }
  } else {
    localStorage.removeItem(TEAMS_TOKEN_KEY);
    localStorage.removeItem(TEAMS_TOKEN_EXPIRY_KEY);
  }
}

export function getTeamsAccessToken(): string | null {
  return localStorage.getItem(TEAMS_TOKEN_KEY);
}

export function clearTeamsAccessToken() {
  localStorage.removeItem(TEAMS_TOKEN_KEY);
  localStorage.removeItem(TEAMS_TOKEN_EXPIRY_KEY);
}

/**
 * Check if the stored Teams token is expired or about to expire.
 */
function isTeamsTokenExpired(): boolean {
  const expiry = localStorage.getItem(TEAMS_TOKEN_EXPIRY_KEY);
  if (!expiry) return true;
  return Date.now() > Number(expiry) - EXPIRY_BUFFER_MS;
}

/**
 * Program Access Result Token Storage
 *
 * Stores a server-signed JWT wrapping the program_access (program filtering outcome) in sessionStorage.
 * Cleared on sign-out and auto-expires with the session.
 */
const SESSION_TOKEN_KEY = "session.token";
const SESSION_TOKEN_EXPIRY_KEY = "session.token.expiry";
const SESSION_TOKEN_EXPIRY_BUFFER_MS = 60 * 1000; // refresh 60 seconds before expiry

export function setSessionToken(token: string | null, expiration?: number) {
  if (token) {
    sessionStorage.setItem(SESSION_TOKEN_KEY, token);
    if (expiration) {
      sessionStorage.setItem(SESSION_TOKEN_EXPIRY_KEY, String(expiration * 1000));
    }
  } else {
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
    sessionStorage.removeItem(SESSION_TOKEN_EXPIRY_KEY);
  }
}

export function getSessionToken(): string | null {
  return sessionStorage.getItem(SESSION_TOKEN_KEY);
}

export function clearSessionToken() {
  sessionStorage.removeItem(SESSION_TOKEN_KEY);
  sessionStorage.removeItem(SESSION_TOKEN_EXPIRY_KEY);
}

export function isSessionTokenExpired(): boolean {
  const expiry = sessionStorage.getItem(SESSION_TOKEN_EXPIRY_KEY);
  if (!expiry) return true;
  return Date.now() > Number(expiry) - SESSION_TOKEN_EXPIRY_BUFFER_MS;
}

export async function getCachedSessionToken(msal: any, settings: any): Promise<string | null> {
    // Only attempt to refresh when:
    // - the session token is expired/missing
    // - we're using B2C with internal programs
    // - MSAL is available and has an active account, OR a Teams token is present
    const shouldUseB2CSession =
        settings?.branding?.YW_USE_AZURE_B2C && settings?.has_internal_programs;
    const hasMsalAccount =
        !!(msal && typeof msal.getActiveAccount === "function" && msal.getActiveAccount());
    const hasTeamsToken = !!getTeamsAccessToken();
    if (isSessionTokenExpired() && shouldUseB2CSession && (hasMsalAccount || hasTeamsToken)) {
        await fetchAndStoreSessionToken(msal, settings);
    }
    return getSessionToken();
}


/**
 * Build the B2C API scope string.
 * Format: https://{tenant}.onmicrosoft.com/{clientId}/access_as_user
 * Fallback: api://{clientId}/access_as_user
 */
export function buildB2CScope(clientId: string, tenantSubdomain?: string): string {
  return tenantSubdomain
    ? `https://${tenantSubdomain}.onmicrosoft.com/${clientId}/access_as_user`
    : `api://${clientId}/access_as_user`;
}

/**
 * Extract B2C claims from the MSAL authentication response,
 * supporting both top-level `idTokenClaims` and `account.idTokenClaims`.
 */
export function getB2CClaims(b2cAuthResponse: any): B2CClaims | null {
  return (b2cAuthResponse?.idTokenClaims ??
    b2cAuthResponse?.account?.idTokenClaims ??
    null) as B2CClaims | null;
}

/**
 * Get an access token for API calls.
 *
 * Checks for Teams token first (stored via setTeamsAccessToken),
 * then falls back to MSAL silent acquisition.
 *
 * If the Teams token is expired, clears it and reloads to trigger re-authentication.
 */
export async function getAccessToken(
  msal: PublicClientApplication | null | undefined,
  settings: any
): Promise<string> {
  const useB2C = settings?.branding?.YW_USE_AZURE_B2C;
  const EASY_AUTH_ENABLED = useB2C ? false : settings?.auth_enabled === "true";

  // For Easy Auth, tokens are handled by Azure App Service - return empty string
  if (EASY_AUTH_ENABLED || !msal) {
    return "";
  }

  // Teams scenario - check for stored Teams token first
  const storedToken = getTeamsAccessToken();
  if (storedToken) {
    if (isTeamsTokenExpired()) {
      // Token expired - clear and force re-auth
      console.log("Teams token expired, triggering re-authentication");
      clearTeamsAccessToken();
      window.location.reload();
    }
    return storedToken;
  }

  // Not in Teams or no stored token - use MSAL to get token
  const account = msal.getActiveAccount();
  if (!account) {
    // User may not have authenticated yet (e.g., browsing programs before B2C login)
    return "";
  }

  const apiClientId = settings?.branding?.azure_b2c_client_id;
  const b2cTenantName = settings?.branding?.b2c_tenant_subdomain || "";
  const scopes = [buildB2CScope(apiClientId, b2cTenantName)];

  try {
    // Attempt to acquire the token silently
    const res = await msal.acquireTokenSilent({ account, scopes });
    return res.accessToken;
  } catch (error) {
    // If silent acquisition fails, redirect for user interaction
    console.error('Silent token acquisition failed:', error);
    if (settings?.has_internal_programs) {
        await msal.acquireTokenRedirect({ scopes });
    }
    return ""; // Return an empty string as a fallback
  }
}
